import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, ShieldPlus, ClipboardCheck, ListChecks, LayoutDashboard, Settings, LogOut, HardHat, Users, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/labels";
import { ScrollText } from "lucide-react";
import { WorkplaceSwitcher } from "@/components/WorkplaceSwitcher";
import { ThemeToggle } from "@/context/ThemeContext";
import { OfflineBanner } from "@/components/OfflineBanner";
import { SiteFooter } from "@/components/SiteFooter";
import { getState } from "@/lib/offline/state";

function navItems(role, platform) {
  const items = [
    { to: "/", label: "Accueil", short: "Accueil", icon: Home, end: true, id: "home" },
    { to: "/take5/nouveau", label: "Nouveau Take 5", short: "Take 5", icon: ShieldPlus, id: "take5" },
    { to: "/equipements", label: "Équipements et véhicules", short: "Contrôler", icon: ClipboardCheck, id: "equipment" },
    { to: "/activites", label: "Mes activités", short: "Activités", icon: ListChecks, id: "activities" },
    { to: "/equipes", label: "Mes équipes", short: "Équipes", icon: Users, id: "teams" },
  ].filter((i) => !(role === "controleur" && i.id === "take5"));
  if (role !== "operateur" && role !== "chef_equipe") items.push({ to: "/supervision", label: "Supervision", short: "Supervision", icon: LayoutDashboard, id: "supervision" });
  if (role === "controleur") items.push({ to: "/journal", label: "Journal", short: "Journal", icon: ScrollText, id: "journal" });
  if (role === "admin") items.push({ to: "/admin", label: "Administration", short: "Admin", icon: Settings, id: "admin" });
  if (platform) items.push({ to: "/plateforme", label: "Plateforme", short: "Plateforme", icon: Building2, id: "platform" });
  return items;
}

const Brand = () => (
  <div className="flex items-center gap-2">
    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-500 text-slate-950"><HardHat className="h-5 w-5" /></div>
    <span className="font-heading text-2xl font-extrabold uppercase tracking-tight">Safe<span className="text-amber-400">Start</span></span>
  </div>
);

export const Avatar = ({ name, small }) => {
  const initials = (name || "?").replace(/\(.*?\)/g, "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <span data-testid="user-avatar" aria-hidden="true" className={`flex shrink-0 items-center justify-center rounded-full bg-amber-500 font-semibold text-slate-950 ${small ? "h-9 w-9 text-xs" : "h-10 w-10 text-sm"}`}>{initials}</span>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = navItems(user.role, user.platform_admin);
  const mobileItems = items.filter((i) => i.id !== "teams" && i.id !== "platform" && (i.id !== "admin" || user.role === "admin")).slice(0, 5);
  const doLogout = async () => {
    const pending = getState().pending;
    if (pending > 0 && !window.confirm(`${pending} élément(s) ne sont pas encore envoyés. Ils restent sur cet appareil et seront envoyés à votre prochaine connexion. Se déconnecter quand même ?`)) return;
    await logout();
    navigate("/connexion");
  };
  const linkCls = ({ isActive }) =>
    `flex min-h-[44px] items-center gap-3 rounded-md px-3 text-sm font-semibold transition-colors ${isActive ? "bg-amber-500/15 text-amber-300 border border-amber-500/40" : "text-slate-300 hover:bg-white/5 border border-transparent"}`;

  return (
    <div className="grain min-h-screen">
      <aside data-testid="desktop-sidebar" className="dark-scope fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-border bg-card p-5 md:flex">
        <Brand />
        <NavLink to="/profil" data-testid="sidebar-profile-link" title="Mon profil" className={({ isActive }) => `mt-6 flex items-center gap-3 rounded-lg border bg-background/60 p-3 transition-colors hover:border-amber-500/50 ${isActive ? "border-amber-500/60" : "border-border"}`}>
          <Avatar name={user.name} />
          <div className="min-w-0">
          <p data-testid="sidebar-user-name" className="truncate text-sm font-semibold">{user.name}</p>
          <p className="truncate text-xs text-muted-foreground">{user.org_name}</p>
          <p data-testid="sidebar-user-role" className="mt-1 font-data text-[11px] uppercase tracking-widest text-amber-400">{roleLabel(user)}</p>
          </div>
        </NavLink>
        <WorkplaceSwitcher />
        <nav className="mt-6 flex flex-1 flex-col gap-1">
          {items.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end} className={linkCls} data-testid={`nav-${i.id}`}>
              <i.icon className="h-4 w-4" /> {i.label}
            </NavLink>
          ))}
        </nav>
        <ThemeToggle withLabel className="flex min-h-[44px] items-center gap-3 rounded-md px-3 text-sm text-slate-400 hover:bg-white/5" />
        <button data-testid="logout-button" onClick={doLogout} className="flex min-h-[44px] items-center gap-3 rounded-md px-3 text-sm text-slate-400 hover:bg-white/5">
          <LogOut className="h-4 w-4" /> Se déconnecter
        </button>
      </aside>

      <header data-testid="mobile-header" className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur-xl md:hidden">
        <Brand />
        <div className="flex items-center gap-2">
          <ThemeToggle testId="mobile-theme-toggle" className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-white/5" />
          {user.role === "admin" && (
            <NavLink to="/admin" data-testid="mobile-admin-link" aria-label="Administration" className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-white/5"><Settings className="h-5 w-5" /></NavLink>
          )}
          <button data-testid="mobile-logout-button" aria-label="Se déconnecter" onClick={doLogout} className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-white/5"><LogOut className="h-5 w-5" /></button>
          <NavLink to="/profil" data-testid="mobile-profile-link" aria-label="Mon profil" className="flex h-11 w-11 items-center justify-center"><Avatar name={user.name} small /></NavLink>
        </div>
      </header>

      <main className="relative z-10 px-4 pb-28 pt-6 md:ml-64 md:px-8 md:pb-12 md:pt-10">
        <div className="mx-auto max-w-6xl"><OfflineBanner /><Outlet /></div>
        <SiteFooter className="-mx-4 mt-12 md:-mx-8" />
      </main>

      <nav data-testid="bottom-navigation" className="fixed inset-x-0 bottom-0 z-30 grid border-t border-border bg-card/95 backdrop-blur-xl md:hidden" style={{ gridTemplateColumns: `repeat(${mobileItems.length}, minmax(0, 1fr))` }}>
        {mobileItems.map((i) => (
          <NavLink key={i.to} to={i.to} end={i.end} data-testid={`bottom-nav-${i.id}`}
            className={({ isActive }) => `flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-semibold ${isActive ? "text-amber-300" : "text-slate-400"}`}>
            <i.icon className="h-5 w-5" /> {i.short}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
